// The video editor's text strings (components/Editor), under t('editor.rve.text.<key>').
// Same keys in en, es, pt and ar; Arabic follows ../../ARABIC_STYLE.md.
export default {
  // components/transcript/transcript-panel.tsx
  transcript: {
    heading: 'Transcript',
    wordsOne: '{n} word',
    wordsMany: '{n} words',
    seconds: '{n}s',
    minutes: '{m}m {s}s',
    editWord: 'Edit word',
    noPauses: 'No long pauses in this clip.',
    cutSpeaker: 'Cut everything {name} says ({n} words)',
    speakerMenu: '{name}: what they say',
    cantListen: "Couldn't listen to this clip's sound. Try again in a moment.",
    emptyTitle: 'No transcript to edit',
    emptyBody: 'The transcript comes from this video’s captions. Add captions and their words show up here: cut a word and it’s cut from the video.',
    removeFillersTitle: 'Cut every um, uh and hmm',
    removeFillers: 'Remove filler words ({n})',
    removePausesTitleClip: 'Listen to the clip and shorten every silence longer than 0.6s',
    removePausesTitle: 'Shorten every silence longer than 0.7s',
    listening: 'Listening for pauses…',
    removePauses: 'Remove pauses',
    removePausesCount: 'Remove pauses ({n} · {duration})',
    emphasizeTitle: "Keywords stay in the captions' highlight colour",
    unmark: 'Unmark',
    emphasize: 'Emphasize',
    cut: 'Cut',
    clearSelection: 'Clear selection',
    hintTitle: 'Click a word to jump to it · shift-click or drag to select · Delete cuts the selection · double-click a word to fix it',
    hint: "Click to jump · {key} cuts · double-click to fix",
    deleteKey: "Delete",
    nothingToRemove: "No filler words or long pauses to remove.",
    hintTouch: "Tap a word to select it · cut or fix it from the bar",
    fixWord: "Fix",
  },

  // components/transcript/transcribe-video.tsx
  transcribe: {
    uploadedVideo: 'Uploaded video',
    jobTitle: 'Captions · {name}',
    video: 'video',
    transcribe: 'Transcribe',
    carriesOn: '{step}… it carries on if you switch panels.',
    dismiss: 'Dismiss',
    usesMinutes: 'Uses your plan’s minutes, like Auto Caption. The captions land on the timeline under the video.',
  },

  // components/overlays/captions/captions-panel.tsx and the caption list
  captions: {
    srtFailed: 'SRT parsing failed with {n} error(s)',
    noCaptionsInSrt: 'No captions found in the SRT file.',
    invalidJson: 'Invalid JSON format. Expected { words: [...] }',
    jsonFailed: 'Failed to parse JSON file. Please check the format.',
    textFailed: 'Failed to read text file.',
    unsupported: 'Unsupported file format. Please use .srt, .json, or .txt',
    line: 'Line {n}',
    moreErrors: '...and {n} more errors',
    dismiss: 'Dismiss',
    processing: 'Processing...',
    upload: 'Upload Caption File',
    supported: 'Supported: .srt, .json, .txt',
    srtHelp: 'SRT files include precise timing for each subtitle. You can export SRT from most transcription tools.',
    or: 'or',
    scriptPlaceholder: 'Type or paste your script here...',
    generate: 'Generate Captions',
    clear: 'Clear',
    label: 'Captions',
    seekTo: 'Seek to {time}',
    start: 'Start',
    end: 'End',
    textPlaceholder: 'Enter caption text...',
  },

  // hooks/use-captions.ts: what is wrong with an uploaded SRT file
  srt: {
    empty: 'File is empty',
    tooShort: 'File too short - must contain at least one subtitle entry',
    noTimestamps: 'No valid SRT timestamps found. Expected format: HH:MM:SS,mmm --> HH:MM:SS,mmm',
    badNumber: 'Invalid subtitle number: "{value}"',
    noTiming: 'Missing timing line',
    badTiming: 'Invalid timing format: "{value}"',
    badTime: 'Invalid time format in subtitle {n}',
    startAfterEnd: 'Start time must be before end time in subtitle {n}',
    noText: 'No subtitle text found for subtitle {n}',
    emptyText: 'Empty subtitle text for subtitle {n}',
    blockFailed: 'Failed to parse subtitle block {n}',
    overlap: 'Subtitle timing overlap detected between subtitle {a} and {b}',
  },

  // components/overlays/captions/captions-error-display.tsx
  srtErrors: {
    title: 'SRT File Parsing Failed',
    file: 'File:',
    foundOne: 'Found {n} error in the SRT file.',
    foundMany: 'Found {n} errors in the SRT file.',
    fixAndRetry: 'Please fix these issues and try again.',
    someSkipped: 'Some subtitles may have been skipped.',
    validation: 'Validation Error',
    format: 'Format Error',
    timing: 'Timing Error',
    encoding: 'Encoding Error',
    error: 'Error',
    solutionsTitle: 'Common Solutions:',
    solutions: [
      'Ensure your SRT file follows the standard format',
      'Check that timestamps use the format HH:MM:SS,mmm → HH:MM:SS,mmm',
      'Verify that subtitle numbers are sequential',
      'Make sure each subtitle block is separated by a blank line',
      'Ensure start times are before end times',
    ],
    expectedFormat: 'Expected SRT Format:',
    tryAnother: 'Try Another File',
  },

  // components/overlays/captions/caption-style-panel.tsx
  captionStyle: {
    templates: {
      minimal: 'Minimal',
      neon: 'Neon',
      tiktok: 'TikTok',
      comic: 'Comic',
      elegant: 'Elegant',
      glitch: 'Glitch',
      bold: 'Bold',
      clean: 'Clean',
      outline: 'Outline',
    },
    cases: {
      normal: 'Normal',
      uppercase: 'UPPERCASE',
      lowercase: 'lowercase',
      capitalize: 'Capitalize',
    },
    animations: {
      fade: 'Fade',
      tiktok: 'TikTok',
      slide: 'Slide',
      pop: 'Pop',
      glitch: 'Glitch',
    },
    text: 'Text',
    highlight: 'Highlight',
    stroke: 'Stroke',
    customize: 'Customize',
    position: 'Position',
    fontAndText: 'Font & Text',
    fontPlaceholder: 'Font...',
    size: 'Size',
    casePlaceholder: 'Case...',
    outline: 'Outline',
    shadow: 'Shadow',
    background: 'Background',
    showBackground: 'Show Background',
    color: 'Color',
    opacity: 'Opacity',
    animationAndTiming: 'Animation & Timing',
    animationPlaceholder: 'Animation...',
    speed: 'Speed',
    highlightScale: 'Highlight Scale ({n}%)',
    words: 'Words',
    spacing: 'Spacing',
    lineHeight: 'Line Height',
    letterSpacing: 'Letter Spacing',
    normalSpacing: 'normal',
    transforms: 'Transforms',
    rotation: 'Rotation',
    skew: 'Skew',
    scale: 'Scale',
  },

  // components/overlays/text: the Text panel and the hook title
  textPanel: {
    elements: 'Elements',
    hookTitle: 'Hook title',
    hookDefault: 'Wait for it…',
    hookOver: '“{text}” over the first 3 seconds',
    templates: {
      modern: { name: 'Modern Title', preview: 'Sleek & Minimalist' },
      impact: { name: 'Impact Statement', preview: 'Bold & Commanding' },
      slicedText: { name: 'Sliced Text', preview: 'Edgy & Modern' },
      minimalMonochrome: { name: 'Minimal Monochrome', preview: 'Ultra Minimal' },
      highlight: { name: 'Highlight Box', preview: 'Modern & Distinctive' },
      gradient: { name: 'Gradient Pop', preview: 'Dynamic & Modern' },
      neonFuture: { name: 'Neon Future', preview: 'Electric & Bold' },
      avantGarde: { name: 'Avant-Garde', preview: 'Fashion Forward' },
      glassmorphic: { name: 'Glassmorphic', preview: 'Modern Transparency' },
    },
  },

  // components/overlays/text: a text layer's own settings in the inspector
  textDetails: {
    preview: 'Text Preview',
    placeholder: 'Enter your text here...',
    settings: 'Settings',
    style: 'Style',
    typography: 'Typography',
    fontFamily: 'Font Family',
    selectFont: 'Select a font',
    fonts: {
      sans: 'Inter (Sans-serif)',
      serif: 'Merriweather (Serif)',
      mono: 'Roboto Mono (Monospace)',
    },
    alignment: 'Alignment',
    alignLeft: 'Align left',
    alignCenter: 'Align center',
    alignRight: 'Align right',
    colors: 'Colors',
    textColor: 'Text Color',
    highlight: 'Highlight',
    gradientNoColors: 'Color settings are not available for gradient text styles',
  },
};
